/**
 * Guest service
 * Handles guest CRUD operations with offline queue support and session management
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  setDoc,
  updateDoc,
  query,
  where,
  Timestamp,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from './firebase';
import { Guest } from '@/types';
import { queueWrite } from '@/pwa/sync';
import { generatePhotoStrip } from '@/utils/photoStrip';

export type PhotoSession = {
  id: string;
  guestId: string;
  guestEmail: string; // Denormalized for convenience
  orgId: string; // Single org ID for this session
  questionnaireId: string;
  responses: Record<string, string | number | boolean | string[]>;
  photoUrls: string[];
  createdAt: Timestamp;
  emailStatus: 'pending' | 'sent' | 'failed';
};

/**
 * Find or create a guest by email
 * Runs in a Cloud Function: the cross-org email lookup needs to read guests
 * that belong to other organizations, which Firestore rules (correctly)
 * forbid on the client. The caller's uid is used as the orgId server-side.
 */
export async function findOrCreateGuest(email: string): Promise<Guest> {
  const callable = httpsCallable(functions, 'findOrCreateGuest');
  const result = await callable({ email });
  const data = result.data as { id: string; email: string; orgIds: string[] };

  return {
    id: data.id,
    email: data.email,
    orgIds: data.orgIds,
    isDeleted: false,
  } as Guest;
}

/**
 * Upload photos to Cloud Storage
 * Path: /orgs/{orgId}/guests/{guestId}/sessions/{sessionId}/{photoIndex}.jpg
 * Also generates and uploads a photo strip as the 4th image
 */
export async function uploadSessionPhotos(
  orgId: string,
  guestId: string,
  sessionId: string,
  photoDataUrls: string[],
  organizationName?: string
): Promise<string[]> {
  // Upload individual photos
  const uploadPromises = photoDataUrls.map(async (dataUrl, index) => {
    // Convert data URL to Blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    // Storage path
    const storagePath = `orgs/${orgId}/guests/${guestId}/sessions/${sessionId}/${index}.jpg`;
    const storageRef = ref(storage, storagePath);

    // Upload
    await uploadBytes(storageRef, blob, {
      contentType: 'image/jpeg',
    });

    // Get download URL
    const downloadUrl = await getDownloadURL(storageRef);
    return downloadUrl;
  });

  const individualPhotoUrls = await Promise.all(uploadPromises);

  // Generate photo strip from the 3 photos
  try {
    const photoStripDataUrl = await generatePhotoStrip(
      photoDataUrls,
      organizationName
    );

    // Convert photo strip to Blob
    const response = await fetch(photoStripDataUrl);
    const blob = await response.blob();

    // Storage path for photo strip
    const stripStoragePath = `orgs/${orgId}/guests/${guestId}/sessions/${sessionId}/strip.jpg`;
    const stripStorageRef = ref(storage, stripStoragePath);

    // Upload photo strip
    await uploadBytes(stripStorageRef, blob, {
      contentType: 'image/jpeg',
    });

    // Get download URL for photo strip
    const stripDownloadUrl = await getDownloadURL(stripStorageRef);

    // Return all photos including the strip at the end
    return [...individualPhotoUrls, stripDownloadUrl];
  } catch (error) {
    console.error('Error generating photo strip:', error);
    // If photo strip generation fails, still return the individual photos
    return individualPhotoUrls;
  }
}

/**
 * Create a new photo session.
 * The caller provides the session ID so a retried submission overwrites the
 * same doc instead of creating a duplicate (which would re-trigger the
 * photo email).
 */
export async function createPhotoSession(
  guestId: string,
  guestEmail: string,
  orgId: string,
  questionnaireId: string,
  responses: Record<string, string | number | boolean | string[]>,
  photoUrls: string[],
  sessionId: string
): Promise<PhotoSession> {
  const newSession: Omit<PhotoSession, 'id'> = {
    guestId,
    guestEmail: guestEmail.toLowerCase().trim(),
    orgId,
    questionnaireId,
    responses,
    photoUrls,
    createdAt: serverTimestamp() as Timestamp,
    emailStatus: 'pending',
  };

  await setDoc(doc(db, `guests/${guestId}/sessions`, sessionId), newSession);

  // Update guest's lastSessionAt
  const guestRef = doc(db, 'guests', guestId);
  await updateDoc(guestRef, {
    lastSessionAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return {
    id: sessionId,
    ...newSession,
  } as PhotoSession;
}

export type SubmitGuestSessionResult = {
  /** True when offline: the session was queued for sync, not submitted */
  queued: boolean;
  guestId: string;
  sessionId: string;
};

/**
 * Complete submission flow: find/create guest, upload photos, create session.
 * Pass the same `sessionId` on a retry to make the whole flow idempotent —
 * photos overwrite the same storage paths and the session doc is overwritten
 * rather than duplicated.
 */
export async function submitGuestSession(
  email: string,
  orgId: string,
  questionnaireId: string,
  responses: Record<string, string | number | boolean | string[]>,
  photoDataUrls: string[],
  providedSessionId?: string
): Promise<SubmitGuestSessionResult> {
  const sessionId = providedSessionId ?? doc(collection(db, 'temp')).id;

  try {
    // 1. Find or create guest (Cloud Function; scoped to the signed-in org)
    const guest = await findOrCreateGuest(email);

    // 3. Get organization name for photo strip
    let organizationName: string | undefined;
    try {
      const orgDoc = await getDoc(doc(db, 'organizations', orgId));
      organizationName = orgDoc.data()?.name;
    } catch (error) {
      console.warn('Could not fetch organization name for photo strip:', error);
    }

    // 4. Upload photos to Storage
    const photoUrls = await uploadSessionPhotos(
      orgId,
      guest.id,
      sessionId,
      photoDataUrls,
      organizationName
    );

    // 5. Create session document
    const session = await createPhotoSession(
      guest.id,
      email,
      orgId,
      questionnaireId,
      responses,
      photoUrls,
      sessionId
    );

    return {
      queued: false,
      guestId: guest.id,
      sessionId: session.id,
    };
  } catch (error) {
    console.error('Error submitting guest session:', error);

    // If offline, queue the write (with the sessionId so the eventual replay
    // stays idempotent with any partial work this attempt already did)
    if (!navigator.onLine) {
      await queueWrite('photoSession', {
        email,
        orgId,
        questionnaireId,
        responses,
        photoDataUrls,
        sessionId,
      });
      return {
        queued: true,
        guestId: 'temp',
        sessionId,
      };
    }
    throw error;
  }
}

/**
 * Get guest by ID
 */
export async function getGuest(guestId: string): Promise<Guest | null> {
  const guestRef = doc(db, 'guests', guestId);
  const snapshot = await getDoc(guestRef);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data() as Guest;
}

/**
 * Get all sessions for a guest
 * Ordered by most recent first
 */
export async function getGuestSessions(
  guestId: string,
  orgId: string
): Promise<PhotoSession[]> {
  const sessionsRef = collection(db, `guests/${guestId}/sessions`);
  // IMPORTANT: Guests can be cross-org. Only query sessions for this org,
  // otherwise the read will fail with permission-denied if any session belongs
  // to a different org.
  const q = query(sessionsRef, where('orgId', '==', orgId));
  const snapshot = await getDocs(q);

  const sessions = snapshot.docs.map(
    (sessionDoc) =>
      ({
        id: sessionDoc.id,
        ...(sessionDoc.data() as Omit<PhotoSession, 'id'>),
      }) as PhotoSession
  );

  sessions.sort((a, b) => {
    const aMillis = a.createdAt?.toMillis?.() ?? 0;
    const bMillis = b.createdAt?.toMillis?.() ?? 0;
    return bMillis - aMillis;
  });

  return sessions;
}

/**
 * Get the number of sessions a guest has for a specific org.
 * Uses Firestore aggregate count() when available.
 */
export async function getGuestSessionCount(
  guestId: string,
  orgId: string
): Promise<number> {
  const sessionsRef = collection(db, `guests/${guestId}/sessions`);
  const q = query(sessionsRef, where('orgId', '==', orgId));

  try {
    const aggregate = await getCountFromServer(q);
    return aggregate.data().count;
  } catch {
    const snapshot = await getDocs(q);
    return snapshot.size;
  }
}

/**
 * Get all guests for an organization
 * Ordered by most recent activity
 * Filters by orgIds array to find guests who have visited this org
 */
export async function getGuestsByOrg(
  orgId: string,
  includeDeleted = false
): Promise<Guest[]> {
  const guestsRef = collection(db, 'guests');
  // NOTE: Avoid composite-index requirements by keeping the Firestore query simple
  // and doing filtering/sorting client-side.
  const q = query(guestsRef, where('orgIds', 'array-contains', orgId));
  const snapshot = await getDocs(q);

  const guests = snapshot.docs.map(
    (guestDoc) =>
      ({
        id: guestDoc.id,
        ...(guestDoc.data() as Omit<Guest, 'id'>),
      }) as Guest
  );

  const filtered = includeDeleted
    ? guests
    : guests.filter((g) => g.isDeleted === false);

  filtered.sort((a, b) => {
    const aMillis = a.lastSessionAt?.toMillis?.() ?? 0;
    const bMillis = b.lastSessionAt?.toMillis?.() ?? 0;
    return bMillis - aMillis;
  });

  return filtered;
}

/**
 * Soft delete guest
 */
export async function deleteGuest(guestId: string): Promise<void> {
  const guestRef = doc(db, 'guests', guestId);
  await updateDoc(guestRef, {
    isDeleted: true,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Restore soft-deleted guest
 */
export async function restoreGuest(guestId: string): Promise<void> {
  const guestRef = doc(db, 'guests', guestId);
  await updateDoc(guestRef, {
    isDeleted: false,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Batch delete multiple guests
 */
export async function batchDeleteGuests(guestIds: string[]): Promise<void> {
  const batch = writeBatch(db);

  for (const guestId of guestIds) {
    const guestRef = doc(db, 'guests', guestId);
    batch.update(guestRef, {
      isDeleted: true,
      updatedAt: Timestamp.now(),
    });
  }

  await batch.commit();
}
